import { DevTool } from '@hookform/devtools'
import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import classNames from 'classnames'
import { JsonEditor } from 'json-edit-react'
import { useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { JsonData } from 'json-edit-react'
import type { ReactElement } from 'react'
import type { IService } from '~/types/application-management'
import type { TPlatformType } from '~/types/device-type'

import Button from '~/components/Buttons/Button'
import CheckboxInput from '~/components/Inputs/ReactHookForm/CheckboxInput'
import TextInput from '~/components/Inputs/ReactHookForm/TextInput'
import Modal from '~/components/Modal'
import { ModalBody, ModalFooter, ModalHeader } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import { keys as appsManagementKeys, useSetServiceMutation } from '~/queries/application-management'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface IFormValues {
  additionalConfig: JsonData
  enabled: boolean
  service: string
}

const validationSchema = Yup.object({
  additionalConfig: Yup.object(),
  enabled: Yup.boolean().required(FORM_ERRORS.REQUIRED),
  service: Yup.string().required(FORM_ERRORS.REQUIRED),
})

interface FormProps {
  appKey: string
  initialServiceConfig?: IService
  onClose: VoidFunction
  platform?: TPlatformType
}

const SetServiceForm = ({ appKey, initialServiceConfig, onClose, platform }: FormProps): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<IFormValues>({
    defaultValues: {
      additionalConfig: !!initialServiceConfig?.additional_config
        ? JSON.parse(initialServiceConfig.additional_config)
        : {},
      enabled: !!initialServiceConfig?.enabled,
      service: initialServiceConfig?.service || '',
    },
    resolver: yupResolver(validationSchema),
  })

  const additionalConfig = watch('additionalConfig')

  /** Queries & Mutations */

  const { mutateAsync: setService } = useSetServiceMutation()

  /** Handlers */

  const handleConfigChange = (data: JsonData) => setValue('additionalConfig', data as object)

  const handleFormSubmit = async (values: IFormValues) => {
    if (platform) {
      try {
        await setService({
          ...values,
          additionalConfig: JSON.stringify(values.additionalConfig),
          appKey,
          platform,
        })
        await queryClient.invalidateQueries({ queryKey: appsManagementKeys.details(appKey) })
        displayToast(
          `Service successfully ${!!initialServiceConfig?.additional_config ? 'updated' : 'created'}`,
          'success'
        )
        onClose()
      } catch (e) {
        captureException(e)
        displayToast(
          `Service could not be ${!!initialServiceConfig?.additional_config ? 'updated' : 'created'}`,
          'error'
        )
      }
    }
  }

  /** Render */

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <ModalBody className="set-service-modal-body">
        <TextInput
          {...register('service')}
          error={errors.service?.message}
          label="Service"
          name="service"
          placeholder="Enter a name for the service"
          required
        />
        <JsonEditor
          className={classNames('json-editor', { 'json-editor--is-erroneous': !!errors.additionalConfig })}
          data={additionalConfig}
          enableClipboard={false}
          rootFontSize="13px"
          rootName="config"
          setData={handleConfigChange}
          showCollectionCount={false}
          theme="githubLight"
        />
        {errors.additionalConfig ? <p className="error-message">{errors.additionalConfig.message}</p> : null}
        <CheckboxInput control={control} label="Enabled" labelPosition="right" name="enabled" />
      </ModalBody>
      <ModalFooter>
        <Button ghost onClick={onClose}>
          Cancel
        </Button>
        <Button disabled={isSubmitting} type="submit">
          Save
        </Button>
      </ModalFooter>
      <DevTool control={control} />
    </form>
  )
}

interface ModalProps extends FormProps {
  isOpen: boolean
}

const SetServiceModal = ({ appKey, initialServiceConfig, isOpen, onClose, platform }: ModalProps): ReactElement => {
  return (
    <Modal className="set-service-modal" isOpen={isOpen} onCloseModal={onClose} shouldCloseOnOverlayClick={false}>
      <ModalHeader>
        {!!initialServiceConfig ? 'Edit' : 'Set'} a service for {platform}
      </ModalHeader>
      <SetServiceForm
        appKey={appKey}
        initialServiceConfig={initialServiceConfig}
        onClose={onClose}
        platform={platform}
      />
    </Modal>
  )
}

export default SetServiceModal
